import { computed, shallowRef, watch } from "vue";
import { toArray } from "#ce/utils/props";

/**
 * Keeps a row per selected or default value so its label survives new result pages. A seed row's
 * wording wins over a fetched one; `order` lists values in selection order.
 */
export function useQuantumSelectSelectionRegistry(props, { selection, defaults, offered, getItemValue }) {
  const retained = shallowRef(new Map());
  const order = shallowRef([]);
  const seeds = computed(() =>
    flatten([...seedRows(props.selectedItems), ...seedRows(props.items)]).filter((row) => row != null),
  );
  const rows = computed(() => [...retained.value.values()]);

  function flatten(rows) {
    return rows.flatMap((row) => [row, ...flatten(Array.isArray(row?.children) ? row.children : [])]);
  }

  function seedRows(value) {
    if (typeof value === "string") {
      try {
        value = JSON.parse(value);
      } catch {
        return [];
      }
    }

    return Array.isArray(value) ? value : [];
  }

  function valuesOf(model) {
    return toArray(model)
      .map(getItemValue)
      .filter((value) => value != null);
  }

  function addToLedger(ledger, seen, value) {
    const key = String(value);

    if (!seen.has(key)) {
      seen.add(key);
      ledger.push(value);
    }
  }

  function remember() {
    const current = valuesOf(selection.value);
    const pinned = valuesOf(defaults.value);
    const keys = new Set([...current, ...pinned].map(String));
    const next = new Map([...retained.value].filter(([key]) => keys.has(key)));
    const offeredRows = flatten(offered.value ?? []);

    for (const value of [...current, ...pinned]) {
      const key = String(value);
      const seed = seeds.value.find((row) => String(getItemValue(row)) === key);
      const fetched = offeredRows.find((row) => String(getItemValue(row)) === key) ?? retained.value.get(key);
      let found = seed ?? fetched;

      if (seed && fetched && typeof seed === "object" && typeof fetched === "object") {
        found = { ...fetched, ...seed };

        if (fetched.props || seed.props) {
          found.props = { ...fetched.props, ...seed.props };
        }
      }

      if (found != null) {
        next.set(key, found);
      }
    }

    retained.value = next;

    const ledger = [...order.value];
    const seen = new Set(ledger.map(String));

    if (!ledger.length) {
      const initialOptions = flatten(seedRows(props.items)).filter(
        (row) => row != null && !row.header && row.selectable !== false,
      );
      const selectedSeeds = seeds.value.filter((row) => keys.has(String(getItemValue(row))));

      for (const row of [...initialOptions, ...selectedSeeds]) {
        const value = getItemValue(row);

        if (value != null) {
          addToLedger(ledger, seen, value);
        }
      }
    }

    for (const value of current) {
      addToLedger(ledger, seen, value);
    }

    order.value = ledger;
  }

  function resolve(value) {
    return retained.value.get(String(value));
  }

  watch([selection, defaults, offered, seeds], remember, { immediate: true, deep: true, flush: "sync" });

  return { resolve, order, rows };
}
